import { useId, useState, type KeyboardEvent } from 'react';
import { splitColors } from '@intentionalist/shared';
import { suggestionList } from './suggest.js';

/**
 * The colours on an item, added one at a time (FR-3.3a, FR-2.11).
 *
 * A thing is often two colours. The field has always been one text box, so
 * "Orange/White" was already being typed into it -- and read as a single value
 * that is a third colour, matching neither Orange nor White when either is
 * asked for.
 *
 * The convention that fixes it is a separator, and a convention is only worth
 * having if nobody has to remember it. So this is not a text box with a rule
 * about slashes: colours are added one at a time and shown as chips, and the
 * separator is something the component writes rather than something the user
 * types. Pasting "Orange/White" still works, because the parser is liberal --
 * it simply becomes two chips.
 *
 * Form state rather than events, unlike tags: colour is a field of the item and
 * is saved with it, which is what keeps one field with one clock.
 */
export function ColorField({
  value,
  onChange,
  suggestions,
}: {
  /** The colours, in order. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Colours already used elsewhere in the catalog. */
  suggestions: string[];
}) {
  const [draft, setDraft] = useState('');
  const inputId = useId();
  const listId = useId();

  const taken = new Set(value.map((color) => color.toLowerCase()));

  // The colours already in the catalog, which are members rather than stored
  // values -- so "Orange/White" is never offered as one. Minus the ones on this
  // item, which are chips above the box rather than something to add again.
  const offered = suggestions.filter((color) => !taken.has(color.toLowerCase()));

  const add = () => {
    // Liberal: one paste of "orange/white" is two colours, not a failed entry.
    const parsed = splitColors(draft).filter((color) => !taken.has(color.toLowerCase()));
    if (parsed.length > 0) onChange([...value, ...parsed]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter adds the colour rather than submitting the surrounding form, which
    // would save the item mid-thought. The separators do the same, so typing
    // the convention out of habit still produces chips.
    if (event.key === 'Enter' || event.key === ',' || event.key === '/') {
      event.preventDefault();
      add();
    }
  };

  return (
    <div className="field">
      <label htmlFor={inputId}>Color</label>

      {value.length > 0 && (
        <ul className="tag-list" aria-label="Colors on this item">
          {value.map((color) => (
            <li key={color.toLowerCase()}>
              <span>{color}</span>
              <button
                type="button"
                aria-label={`Remove color ${color}`}
                onClick={() => onChange(value.filter((other) => other !== color))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tag-add">
        <input
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Type a color and press Enter"
          autoComplete="off"
          {...suggestionList(listId, draft, offered)}
        />
        <datalist id={listId}>
          {offered.map((color) => (
            <option key={color} value={color} />
          ))}
        </datalist>
        {/* Named for what it adds; the tag editor's Add sits beside it. */}
        <button type="button" aria-label="Add color" onClick={add} disabled={draft.trim() === ''}>
          Add
        </button>
      </div>
    </div>
  );
}
